import { useEffect, useState } from 'react';

/**
 * 몇십 초마다 다시 읽을 때 쓰는 판 번호 (44회차).
 *
 * 43회차까지 서비스 목록·트레이스만 추이·관제 서버 상태가 저마다 `setInterval`을 걸어
 * 탭이 숨어 있어도 30초마다 불렀다. 관제 화면은 대개 늘 열어 두는 창이라 아무도 안 보는
 * 동안의 조회가 계속 쌓인다. 여기서는 **탭이 숨은 동안은 판을 올리지 않고**, 다시 보이는 순간
 * 마지막으로 올린 뒤 주기가 지났으면 곧바로 한 번 올린다 — 돌아온 사람이 옛 숫자를 보지 않게.
 *
 * "지났는지"는 시각으로 본다. 브라우저가 숨은 탭의 타이머를 아예 멈추면(동결) 건너뛴 기록조차
 * 남지 않기 때문이다. 곧바로 올린 뒤에는 주기를 새로 걸어 바로 이어 한 번 더 읽지 않는다.
 *
 * 돌려주는 값을 `useQuery`의 `version`에 넘기면 된다.
 */
export function usePolling(intervalMs: number): number {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (intervalMs <= 0) return;
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    let lastBump = Date.now();
    let timer: ReturnType<typeof setInterval> | undefined;
    const bump = () => {
      lastBump = Date.now();
      setVersion((value) => value + 1);
    };
    const schedule = () => {
      if (timer !== undefined) clearInterval(timer);
      timer = setInterval(() => {
        if (!hidden()) bump();
      }, intervalMs);
    };
    const onVisibility = () => {
      if (hidden() || Date.now() - lastBump < intervalMs) return;
      bump();
      schedule();
    };
    schedule();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    return () => {
      if (timer !== undefined) clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [intervalMs]);

  return version;
}
